import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Check, CalendarClock, ArrowRight } from "lucide-react";

// ---------------------------------------------------------------------------
// Action Plan persistence
// ---------------------------------------------------------------------------
// localStorage shape:
//   cfograde_action_plan_v1 = {
//     [reportId]: {
//       started_at: "<iso>",          // first time the user checked anything
//       done: { [ideaTitle]: true },  // checked ideas
//     }
//   }
// We key by reportId so re-runs start a fresh plan; the user can compare
// progress quarter-over-quarter from the Trends page.
const STORAGE_KEY = "cfograde_action_plan_v1";

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage unavailable — silently no-op */
  }
}

function loadPlan(reportId) {
  if (!reportId) return { started_at: null, done: {} };
  const store = readStore();
  return store[reportId] || { started_at: null, done: {} };
}
function savePlan(reportId, plan) {
  if (!reportId) return;
  const store = readStore();
  store[reportId] = plan;
  writeStore(store);
}

const IDEA_CATEGORY_META = {
  margin: { label: "Margin", color: "text-[#C26D5C] bg-[#C26D5C]/10 border-[#C26D5C]/30" },
  working_capital: { label: "Working Capital", color: "text-[#1F382E] bg-[#1F382E]/8 border-[#1F382E]/20" },
  leverage: { label: "Leverage", color: "text-[#9c6b1d] bg-[#9c6b1d]/10 border-[#9c6b1d]/30" },
  liquidity: { label: "Liquidity", color: "text-[#2C5C43] bg-[#2C5C43]/10 border-[#2C5C43]/30" },
  growth: { label: "Growth", color: "text-[#2C5C43] bg-[#2C5C43]/10 border-[#2C5C43]/30" },
  risk: { label: "Risk", color: "text-[#A84632] bg-[#A84632]/10 border-[#A84632]/30" },
};
const IDEA_PRIORITY_META = {
  high: { label: "High priority", dot: "bg-[#A84632]" },
  medium: { label: "Medium priority", dot: "bg-[#9c6b1d]" },
  low: { label: "Low priority", dot: "bg-[#5C665D]" },
};

function daysBetween(iso, now = new Date()) {
  if (!iso) return null;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return null;
  return Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

export default function ImprovementIdeasGrid({ ideas, reportId }) {
  const [plan, setPlan] = useState(() => loadPlan(reportId));

  // Reload when reportId changes (e.g. user opens a different report from history)
  useEffect(() => {
    setPlan(loadPlan(reportId));
  }, [reportId]);

  const sorted = useMemo(
    () =>
      [...ideas].sort((a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
      }),
    [ideas]
  );

  const doneCount = sorted.filter((i) => plan.done?.[i.title]).length;
  const total = sorted.length;
  const pct = total === 0 ? 0 : Math.round((doneCount / total) * 100);

  function toggle(title) {
    const isDone = !!plan.done?.[title];
    const nextDone = { ...(plan.done || {}) };
    if (isDone) {
      delete nextDone[title];
    } else {
      nextDone[title] = true;
    }
    const nextPlan = {
      ...plan,
      started_at: plan.started_at || new Date().toISOString(),
      done: nextDone,
    };
    setPlan(nextPlan);
    savePlan(reportId, nextPlan);
  }

  const daysIn = daysBetween(plan.started_at);
  const showRerunNudge = doneCount > 0 && daysIn !== null && daysIn >= 60;
  const showStartedHeader = doneCount > 0;

  return (
    <div data-testid="improvement-ideas-wrap">
      {showStartedHeader && (
        <ActionPlanHeader
          doneCount={doneCount}
          total={total}
          pct={pct}
          daysIn={daysIn}
          showRerunNudge={showRerunNudge}
        />
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="improvement-ideas">
        {sorted.map((idea, i) => {
          const cat = IDEA_CATEGORY_META[idea.category] || IDEA_CATEGORY_META.growth;
          const pri = IDEA_PRIORITY_META[idea.priority] || IDEA_PRIORITY_META.medium;
          const isDone = !!plan.done?.[idea.title];
          return (
            <div
              key={`${idea.title}-${i}`}
              className={`bg-white border rounded-2xl p-5 sm:p-6 flex flex-col transition-all duration-300 ${
                isDone
                  ? "border-[#2C5C43]/40 bg-[#2C5C43]/[0.03]"
                  : "border-[#E5E0D8]"
              }`}
              data-testid={`improvement-idea-${i}`}
              data-done={isDone ? "true" : "false"}
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <span
                  className={`text-[10px] uppercase tracking-[0.16em] border rounded-full px-2 py-0.5 ${cat.color}`}
                >
                  {cat.label}
                </span>
                <button
                  type="button"
                  onClick={() => toggle(idea.title)}
                  aria-pressed={isDone}
                  data-testid={`improvement-idea-toggle-${i}`}
                  className={`no-print group flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1 border transition-all duration-200 ${
                    isDone
                      ? "border-[#2C5C43]/40 bg-[#2C5C43]/10 text-[#2C5C43] hover:bg-[#2C5C43]/15"
                      : "border-[#E5E0D8] text-[#5C665D] hover:border-[#1F382E]/30 hover:text-[#1F382E]"
                  }`}
                >
                  <span
                    className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border transition-colors ${
                      isDone
                        ? "bg-[#2C5C43] border-[#2C5C43] text-[#F7F5F0]"
                        : "border-[#C5BFB3] bg-white"
                    }`}
                  >
                    {isDone && <Check size={10} strokeWidth={3} />}
                  </span>
                  {isDone ? "Done" : "Mark done"}
                </button>
              </div>

              {/* Hidden in-print priority dot — preserves info density on PDF */}
              <p className="print-only text-[10px] text-[#5C665D] mb-1 flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${pri.dot}`} />
                {pri.label}
              </p>
              <p className="no-print text-[10px] text-[#5C665D] -mt-1 mb-1.5 flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${pri.dot}`} />
                {pri.label}
              </p>

              <h3
                className={`font-serif-display text-lg leading-tight ${
                  isDone ? "text-[#5C665D] line-through" : "text-[#1F382E]"
                }`}
              >
                {idea.title}
              </h3>
              <p
                className={`text-[13.5px] leading-relaxed mt-2 ${
                  isDone ? "text-[#5C665D]" : "text-[#1A1A1A]"
                }`}
              >
                {idea.why}
              </p>
              {Array.isArray(idea.how) && idea.how.length > 0 && (
                <ol
                  className={`mt-3.5 space-y-1.5 text-[13px] list-decimal pl-4 marker:text-[#5C665D] ${
                    isDone ? "text-[#5C665D]" : "text-[#1A1A1A]"
                  }`}
                >
                  {idea.how.map((step, j) => (
                    <li key={j} className="leading-relaxed pl-1">
                      {step}
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-auto pt-4 border-t border-dashed border-[#E5E0D8] flex items-center justify-between text-[12px]">
                <span className="text-[#5C665D]">{idea.timeline}</span>
                <span
                  className={`font-medium ${
                    isDone ? "text-[#2C5C43]" : "text-[#1F382E]"
                  }`}
                >
                  {idea.impact_label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionPlanHeader({ doneCount, total, pct, daysIn, showRerunNudge }) {
  const daysCopy =
    daysIn === 0
      ? "started today"
      : daysIn === 1
        ? "1 day in"
        : `${daysIn} days in`;
  return (
    <div
      className="no-print bg-white border border-[#E5E0D8] rounded-2xl p-4 sm:p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="action-plan-header"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
            Your action plan · {daysCopy}
          </p>
          <p
            className="text-[12px] text-[#1F382E] font-medium tabular-nums"
            data-testid="action-plan-progress-text"
          >
            {doneCount} of {total} done
          </p>
        </div>
        <div
          className="h-1.5 bg-[#E5E0D8] rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          data-testid="action-plan-progress-bar"
        >
          <div
            className="h-full bg-[#2C5C43] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {showRerunNudge && (
        <Link to="/analyze" className="shrink-0">
          <Button
            variant="outline"
            className="border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-10 px-4 transition-all duration-300 whitespace-nowrap"
            data-testid="action-plan-rerun-cta"
          >
            <CalendarClock size={14} strokeWidth={1.5} className="mr-2" />
            Re-run to track wins
            <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
          </Button>
        </Link>
      )}
    </div>
  );
}
